import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import LockClosedIcon from '@/assets/icons/lock-closed.svg';
import CueAvatar from '@/assets/cue-avatar.png';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import {
  FREEMIUM_MULTI_OPENING_CUE_MESSAGE,
  PRESET_CUE_MESSAGE_PARAM,
  writeCueSelectedOpenings,
} from '@/utils/cue-handoff';

import type { OpeningInfo } from '../types';

interface FreemiumMultiOpeningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOpeningIds: string[];
  openings: Record<string, OpeningInfo>;
}

export function FreemiumMultiOpeningDialog({
  open,
  onOpenChange,
  selectedOpeningIds,
  openings,
}: FreemiumMultiOpeningDialogProps) {
  const navigate = useNavigate();
  const { allCampaignsPath } = useNavigationPaths();
  const count = selectedOpeningIds.length;

  const handleContinueWithCue = () => {
    // Cue is hidden on this route. Stage openings, then land where the host
    // can open Cue and send the preset message.
    writeCueSelectedOpenings(
      selectedOpeningIds.map(id => ({
        id,
        name: openings[id]?.title ?? id,
      })),
    );

    const params = new URLSearchParams({
      [PRESET_CUE_MESSAGE_PARAM]: FREEMIUM_MULTI_OPENING_CUE_MESSAGE,
    });
    navigate(`${allCampaignsPath}?${params.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="freemium-multi-opening-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Multiple openings selected"
              description="Title of the dialog when a free-plan user selects more than one opening to post"
            />
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-2 p-6">
          <div className="flex items-center gap-2">
            <img src={LockClosedIcon} alt="" className="size-4 shrink-0" />
            <Typography variant="subtitle2">
              <FormattedMessage
                defaultMessage="Self-posting is limited to one opening"
                description="Subhead explaining the free-plan limit of posting one opening at a time"
              />
            </Typography>
          </div>
          <Typography variant="subtitle1" as="p">
            <FormattedMessage
              defaultMessage="On the free plan you can post one opening yourself at a time. Cue can post all <b>{count}</b> for you at no cost — or go back and select a single opening to post manually."
              description="Body of the free-plan multi-opening dialog. {count} is the number of selected openings; <b> bolds that number."
              values={{
                count,
                b: (chunks: ReactNode) => (
                  <span className="font-bold">{chunks}</span>
                ),
              }}
            />
          </Typography>
        </DialogBody>

        <div className="border-fx-neutral-200 flex shrink-0 items-center justify-between rounded-b-lg border-t bg-white p-2">
          <DialogClose asChild>
            <Button
              variant="secondary"
              type="button"
              data-testid="freemium-multi-opening-back"
            >
              <FormattedMessage
                defaultMessage="Back"
                description="Dismiss the free-plan multi-opening dialog and return to opening selection"
              />
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleContinueWithCue}
            data-testid="continue-posting-with-cue"
          >
            <img src={CueAvatar} alt="" className="size-4 rounded-full" />
            <FormattedMessage
              defaultMessage="Continue posting with Cue"
              description="Primary action: leave the posting flow and ask Cue to post the selected openings"
            />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
